// Shared lifecycle resolver for v0.3 property buildStatus + legacy farms.status.
//
// Source of truth: `marcusIngestion.js → mirrorConfigToFarm` writes both
// fields onto each farm doc:
//   - farm.buildStatus  → v0.3 lifecycle (intake_submitted | in_build | ready
//                          | build_failed | build_paused_cancellation)
//   - farm.status        → legacy uppercase enum (READY | PROCESSING | ERROR
//                          | UPLOADED | COMPLETED), the only field present
//                          on the 5 pre-v0.3 farms (ASB / BRI / DHT / PCF / TOV)
//
// The five legacy farms do NOT yet have buildStatus. To avoid breaking them,
// when buildStatus is absent we fall back to the legacy enum, and anything
// that isn't explicitly an error / processing legacy state is treated as
// ready (these properties have been working in production for months).
//
// Used by: Layout sidebar dot, FarmCard pill, MapViewer / FarmAnalytics
// gate, PropertyBeingPreparedTile. Keep all status surfaces routed through
// here so taxonomy + tone + label stay consistent.

export const BUILD_STATUS = Object.freeze({
  INTAKE_SUBMITTED: 'intake_submitted',
  IN_BUILD: 'in_build',
  READY: 'ready',
  BUILD_FAILED: 'build_failed',
  BUILD_PAUSED_CANCELLATION: 'build_paused_cancellation',
});

const KNOWN_BUILD_STATUSES = new Set(Object.values(BUILD_STATUS));

/**
 * @typedef {Object} LifecycleState
 * @property {string} key            One of: 'ready' | 'in_build' | 'intake_submitted'
 *                                   | 'build_failed' | 'build_paused_cancellation'
 *                                   | 'legacy_ready' | 'legacy_processing' | 'legacy_error'
 *                                   | 'unknown'
 * @property {string} label          Short user-facing label.
 * @property {'ready'|'preparing'|'failed'|'paused'|'unknown'} tone
 * @property {boolean} isReady       Safe to render full analytics / map.
 * @property {boolean} isPreparing   In-build, intake_submitted, legacy PROCESSING.
 * @property {boolean} isBlocked     Failed or paused — admin action required.
 * @property {string} dotClass       Tailwind classes for a small status dot.
 * @property {string} pillClass      Tailwind classes for a status-pill background.
 * @property {string} textClass      Tailwind text colour to pair with `pillClass`.
 */

/**
 * Resolve the lifecycle state of a farm/property doc.
 * Defensive: returns 'unknown' for null/undefined/non-object inputs.
 *
 * @param {object | null | undefined} farm  Firestore `farms/{id}` document.
 * @returns {LifecycleState}
 */
export function resolveLifecycleState(farm) {
  if (!farm || typeof farm !== 'object') {
    return {
      key: 'unknown',
      label: 'Unknown',
      tone: 'unknown',
      isReady: false,
      isPreparing: false,
      isBlocked: false,
      dotClass: 'bg-slate-300',
      pillClass: 'bg-slate-50',
      textClass: 'text-slate-500',
    };
  }

  const bs = typeof farm.buildStatus === 'string' && KNOWN_BUILD_STATUSES.has(farm.buildStatus)
    ? farm.buildStatus
    : null;

  if (bs === BUILD_STATUS.READY) {
    return {
      key: 'ready', label: 'Ready', tone: 'ready',
      isReady: true, isPreparing: false, isBlocked: false,
      dotClass: 'bg-emerald-500',
      pillClass: 'bg-green-50', textClass: 'text-green-600',
    };
  }
  if (bs === BUILD_STATUS.IN_BUILD) {
    return {
      key: 'in_build', label: 'Building', tone: 'preparing',
      isReady: false, isPreparing: true, isBlocked: false,
      dotClass: 'bg-amber-400 animate-pulse',
      pillClass: 'bg-amber-50', textClass: 'text-amber-600',
    };
  }
  if (bs === BUILD_STATUS.INTAKE_SUBMITTED) {
    return {
      key: 'intake_submitted', label: 'Queued', tone: 'preparing',
      isReady: false, isPreparing: true, isBlocked: false,
      dotClass: 'bg-amber-400',
      pillClass: 'bg-amber-50', textClass: 'text-amber-600',
    };
  }
  if (bs === BUILD_STATUS.BUILD_FAILED) {
    return {
      key: 'build_failed', label: 'Build failed', tone: 'failed',
      isReady: false, isPreparing: false, isBlocked: true,
      dotClass: 'bg-red-500',
      pillClass: 'bg-red-50', textClass: 'text-red-600',
    };
  }
  if (bs === BUILD_STATUS.BUILD_PAUSED_CANCELLATION) {
    return {
      key: 'build_paused_cancellation', label: 'Paused', tone: 'paused',
      isReady: false, isPreparing: false, isBlocked: true,
      dotClass: 'bg-amber-400',
      pillClass: 'bg-amber-50', textClass: 'text-amber-700',
    };
  }

  // No v0.3 buildStatus → legacy farm. Map old uppercase enum.
  const legacy = typeof farm.status === 'string' ? farm.status : null;
  if (legacy === 'ERROR') {
    return {
      key: 'legacy_error', label: 'Error', tone: 'failed',
      isReady: false, isPreparing: false, isBlocked: true,
      dotClass: 'bg-red-500',
      pillClass: 'bg-red-50', textClass: 'text-red-600',
    };
  }
  if (legacy === 'PROCESSING' || legacy === 'UPLOADED' || legacy === 'PENDING') {
    return {
      key: 'legacy_processing',
      label: legacy === 'PROCESSING' ? 'Processing' : legacy === 'UPLOADED' ? 'Uploaded' : 'Pending',
      tone: 'preparing',
      isReady: false, isPreparing: true, isBlocked: false,
      dotClass: 'bg-amber-400',
      pillClass: 'bg-amber-50', textClass: 'text-amber-600',
    };
  }

  // Default: legacy READY / COMPLETED / undefined → ready, so the 5 pre-v0.3
  // farms keep rendering exactly as before.
  return {
    key: 'legacy_ready', label: legacy || 'Ready', tone: 'ready',
    isReady: true, isPreparing: false, isBlocked: false,
    dotClass: 'bg-emerald-500',
    pillClass: 'bg-green-50', textClass: 'text-green-600',
  };
}
